import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Inicio from './pages/Inicio';
import Dashboard from './pages/Dashboard';
import KPIs from './pages/KPIs';
import KPITrash from './pages/KPITrash';
import Capture from './pages/Capture';
import CalendarPage from './pages/Calendar';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Permissions from './pages/Permissions';
import Teams from './pages/Teams';
import Settings from './pages/Settings';
import MenuPermissions from './pages/MenuPermissions';
import OrgChart from './pages/OrgChart';
import Colaboradores from './pages/capitalhumano/Colaboradores';
import Vacantes from './pages/capitalhumano/Vacantes';
import ProveedoresVisitas from './pages/vigilancia/ProveedoresVisitas';
import HerramentalesVig from './pages/vigilancia/Herramentales';
import PersonalVigilancia from './pages/vigilancia/PersonalVigilancia';
// Help Desk Module
import { 
  HelpdeskDashboard, 
  TicketDetail, 
  HelpdeskCategories, 
  KnowledgeBase, 
  HelpdeskReports 
} from './pages/helpdesk/index';
import EmailLogs from './pages/helpdesk/EmailLogs';
import HelpdeskSettings from './pages/helpdesk/Settings';
import PermissionGuard from './components/PermissionGuard';
import './App.css';

// Redirect component that properly handles dynamic params
const AnalysisRedirect = () => {
  const { kpiId } = useParams();
  return <Navigate to={`/kpis/${kpiId}`} replace />;
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/inicio" replace />;
  }

  return children;
};

// Beta Protected Route - For modules that may be in BETA mode.
// For non-admin users, granular permissions (Phase 2) take precedence when
// a granular module/sub is provided. If granular grants 'basic' or 'full',
// the beta gate is bypassed entirely (beta is an admin-only rollout concept).
// Granular 'none' is handled by <PermissionGuard>, not here.
const BetaProtectedRoute = ({ children, moduleKey, subKey, granularModule, granularSub }) => {
  const { isAuthenticated, loading, isAdmin, user } = useAuth();
  const granularCanAccess = React.useMemo(() => {
    if (!granularModule) return null;
    if (isAdmin()) return true;
    const perms = user?.permissions || {};
    const node = perms[granularModule];
    if (node == null) return false;
    if (typeof node === 'string') return node !== 'none';
    if (!granularSub) return Object.values(node).some((v) => v && v !== 'none');
    return node[granularSub] && node[granularSub] !== 'none';
  }, [isAdmin, user?.permissions, granularModule, granularSub]);
  const [hasAccess, setHasAccess] = React.useState(null);
  const [checkingAccess, setCheckingAccess] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      // Admin always has access
      if (isAdmin()) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      // Granular permission short-circuits the beta gate when defined
      if (granularCanAccess !== null) {
        setHasAccess(true); // granular 'none' is caught by PermissionGuard below
        setCheckingAccess(false);
        return;
      }

      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        let url = `${API_URL}/api/menu-permissions/check/${moduleKey}`;
        if (subKey) {
          url += `?sub_key=${subKey}`;
        }
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setHasAccess(data.has_access);
      } catch (error) {
        console.error('Error checking menu access:', error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    if (isAuthenticated && !loading) {
      checkAccess();
    }
  }, [isAuthenticated, loading, isAdmin, moduleKey, subKey, granularCanAccess]);

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return <Navigate to="/inicio" replace />;
  }

  return children;
};

// Public Route Component (redirect to inicio if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/inicio" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/inicio"
        element={
          <ProtectedRoute>
            <Inicio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PermissionGuard module="kpis" sub="dashboard">
              <Dashboard />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpis"
        element={
          <ProtectedRoute>
            <PermissionGuard module="kpis" sub="kpis">
              <KPIs />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpis/:kpiId"
        element={
          <ProtectedRoute>
            <PermissionGuard module="kpis" sub="kpis">
              <KPIs />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpis/trash"
        element={
          <ProtectedRoute adminOnly>
            <KPITrash />
          </ProtectedRoute>
        }
      />
      <Route
        path="/capture"
        element={
          <ProtectedRoute>
            <PermissionGuard module="kpis" sub="captura">
              <Capture />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <PermissionGuard module="kpis" sub="calendario">
              <CalendarPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/roles"
        element={
          <ProtectedRoute adminOnly>
            <Roles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/permissions"
        element={
          <ProtectedRoute adminOnly>
            <Permissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/teams"
        element={
          <ProtectedRoute adminOnly>
            <Teams />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute adminOnly>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/menus"
        element={
          <ProtectedRoute adminOnly>
            <MenuPermissions />
          </ProtectedRoute>
        }
      />
      {/* Capital Humano Module Routes */}
      <Route
        path="/capital-humano/organigrama"
        element={
          <ProtectedRoute>
            <PermissionGuard module="capital_humano" sub="organigrama">
              <OrgChart />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capital-humano/colaboradores"
        element={
          <ProtectedRoute>
            <PermissionGuard module="capital_humano" sub="colaboradores">
              <Colaboradores />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capital-humano/vacantes"
        element={
          <ProtectedRoute>
            <PermissionGuard module="capital_humano" sub="vacantes">
              <Vacantes />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      {/* Legacy redirect: Organigrama moved under Capital Humano */}
      <Route path="/organigrama" element={<Navigate to="/capital-humano/organigrama" replace />} />

      {/* Vigilancia Module Routes */}
      <Route
        path="/vigilancia/proveedores-visitas"
        element={
          <ProtectedRoute>
            <PermissionGuard module="vigilancia" sub="proveedores_visitas">
              <ProveedoresVisitas />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vigilancia/herramentales"
        element={
          <ProtectedRoute>
            <PermissionGuard module="vigilancia" sub="herramentales">
              <HerramentalesVig />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vigilancia/personal"
        element={
          <ProtectedRoute>
            <PermissionGuard module="vigilancia" sub="personal">
              <PersonalVigilancia />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      
      {/* Help Desk Module Routes - Protected with sub-module-level BETA check */}
      <Route
        path="/helpdesk"
        element={
          <BetaProtectedRoute moduleKey="helpdesk" subKey="helpdesk_dashboard" granularModule="mesa_de_ayuda" granularSub="dashboard">
            <PermissionGuard module="mesa_de_ayuda" sub="dashboard">
              <HelpdeskDashboard />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/tickets/:ticketId"
        element={
          <BetaProtectedRoute moduleKey="helpdesk" subKey="helpdesk_dashboard" granularModule="mesa_de_ayuda" granularSub="dashboard">
            <PermissionGuard module="mesa_de_ayuda" sub="dashboard">
              <TicketDetail />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/categories"
        element={
          <BetaProtectedRoute moduleKey="helpdesk" subKey="helpdesk_categories" granularModule="mesa_de_ayuda" granularSub="categorias">
            <PermissionGuard module="mesa_de_ayuda" sub="categorias">
              <HelpdeskCategories />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />
      <Route
        path="/knowledge"
        element={
          <BetaProtectedRoute moduleKey="knowledge" granularModule="base_de_conocimiento">
            <PermissionGuard module="base_de_conocimiento">
              <KnowledgeBase />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/reports"
        element={
          <BetaProtectedRoute moduleKey="helpdesk" subKey="helpdesk_reports" granularModule="mesa_de_ayuda" granularSub="informes">
            <PermissionGuard module="mesa_de_ayuda" sub="informes">
              <HelpdeskReports />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/email-logs"
        element={
          <BetaProtectedRoute moduleKey="helpdesk" subKey="helpdesk_email_logs" granularModule="mesa_de_ayuda" granularSub="logs_email">
            <PermissionGuard module="mesa_de_ayuda" sub="logs_email">
              <EmailLogs />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/settings"
        element={
          <BetaProtectedRoute moduleKey="helpdesk" subKey="helpdesk_settings" granularModule="mesa_de_ayuda" granularSub="configuracion">
            <PermissionGuard module="mesa_de_ayuda" sub="configuracion">
              <HelpdeskSettings />
            </PermissionGuard>
          </BetaProtectedRoute>
        }
      />

      {/* Legacy redirect for analysis */}
      <Route path="/analysis" element={<Navigate to="/kpis" replace />} />
      <Route path="/analysis/:kpiId" element={<AnalysisRedirect />} />

      {/* Redirect root to inicio */}
      <Route path="/" element={<Navigate to="/inicio" replace />} />
      
      {/* 404 - Redirect to inicio */}
      <Route path="*" element={<Navigate to="/inicio" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
